import { useQuery } from 'app/components/Contexts';
import { useHistory } from 'app/components/router/History';
import { PositiveIntegerCodec, EntityStringIdRouteCodec } from 'app/constants/Routes/codecs';
import { mergeQuery, ISetQueryOptions } from 'app/models/route/actions';
import { getWithDefault } from 'app/utils/fpTs';
import { decodeWithDefault, decodeOrThrow } from 'app/utils/ioTs';
import { parseBoolean } from 'app/utils/parse';
import { useMemoOne } from 'app/utils/useMemoOne';
import React, { useCallback, useContext } from 'react';
import { EntityStringId } from 'app/types/entity';

// These values come directly from `query-string` module
export interface ISearchObject {
  [key: string]: string | string[] | undefined | null;
}

export interface IQueryParser<ValueType> {
  serialize: (value: ValueType) => ISearchObject;
  deserialize: (query: ISearchObject) => ValueType;
  isEqual?: (a: ValueType, b: ValueType) => boolean;
}

export function makeStringParser(paramName: string): IQueryParser<string | undefined>;
export function makeStringParser(paramName: string, defaultValue: string): IQueryParser<string>;
export function makeStringParser(
  paramName: string,
  defaultValue?: string,
): IQueryParser<string> | IQueryParser<string | undefined> {
  return {
    serialize: (value: string | undefined) => ({ [paramName]: value }),
    deserialize: (query) => {
      const param = query[paramName];
      return Array.isArray(param) ? defaultValue : (param ?? defaultValue);
    },
  };
}

export const makeArrayParser = (paramName: string): IQueryParser<string[] | undefined> => ({
  serialize: value => ({ [paramName]: value }),
  deserialize: query => {
    const param = query[paramName];
    if (param == null) return [];
    return Array.isArray(param) ? param : [param];
  },
});

export const makeObjectParser = (
  paramName: string,
): IQueryParser<{ [key: string]: unknown } | undefined> => ({
  serialize: value => ({ [paramName]: value != null ? JSON.stringify(value) : undefined }),
  deserialize: (query): { [key: string]: unknown } | undefined => {
    const param = query[paramName];
    if (param == null || Array.isArray(param)) return undefined;
    try {
      return JSON.parse(param);
    } catch {
      return;
    }
  },
});

export function makeEntityStringIdParser(paramName: string): IQueryParser<EntityStringId | undefined> {
  return {
    serialize: (value: EntityStringId | undefined) => ({
      [paramName]: value ? EntityStringIdRouteCodec.encode(value) : undefined,
    }),
    deserialize: (query) => {
      try {
        return decodeOrThrow(EntityStringIdRouteCodec, query[paramName]);
      } catch (e) {
        return undefined;
      }
    },
  };
}

export const makeDateParser = (paramName: string): IQueryParser<Date | null> => ({
  serialize: (value) => ({ [paramName]: value ? value.toISOString() : undefined }),
  deserialize: (query) => {
    const dateString = query[paramName];
    const value = new Date(typeof dateString === 'string' ? dateString : NaN);
    // Ignore invalid dates objects.
    return !isNaN(value.getTime()) ? value : null;
  },
});

export function makeBooleanParser(paramName: string): IQueryParser<boolean | undefined>;
export function makeBooleanParser(paramName: string, defaultValue: boolean): IQueryParser<boolean>;
export function makeBooleanParser(
  paramName: string,
  defaultValue?: boolean,
): IQueryParser<boolean> | IQueryParser<boolean | undefined> {
  return {
    serialize: (value: boolean | undefined) => ({
      [paramName]: value != null ? String(value) : undefined,
    }),
    deserialize: query => {
      const param = query[paramName];
      if (param == null || Array.isArray(param)) return defaultValue;
      return getWithDefault(parseBoolean(param), defaultValue);
    },
  };
}

export function makePositiveIntegerParser(paramName: string): IQueryParser<number | undefined>;
export function makePositiveIntegerParser(paramName: string, defaultValue: number): IQueryParser<number>;
export function makePositiveIntegerParser(
  paramName: string,
  defaultValue?: number,
): IQueryParser<number | undefined> | IQueryParser<number> {
  return {
    serialize: (value: number | undefined) => ({
      [paramName]: value != null && Number.isInteger(value) ? String(value) : undefined,
    }),
    deserialize: query => decodeWithDefault(PositiveIntegerCodec, query[paramName], defaultValue),
  };
}

export function createParamContext<ValueType>(
  queryParser: IQueryParser<ValueType>,
) {
  const isEqual = queryParser.isEqual || ((a: unknown, b: unknown): boolean => a === b);
  const defaultValue = queryParser.deserialize({});

  type SetValueType = (value: ValueType, options?: ISetQueryOptions) => void;
  const ParamContext = React.createContext<readonly [ValueType, SetValueType]>([
    defaultValue,
    () => { throw new Error('Not implemented'); },
  ]);

  const Provider = ({ children }: { children: React.ReactNode }) => {
    const history = useHistory();
    const query = useQuery();

    const value = useMemoOne(() => queryParser.deserialize(query), [query]);

    const setValue = useCallback((newValue: ValueType, options?: ISetQueryOptions) => {
      mergeQuery(history, queryParser.serialize(newValue), options);
    }, [history]);

    const memoizedValue = useMemoOne(
      () => value,
      [value],
      isEqual,
    );

    const contextValue = useMemoOne(
      () => [memoizedValue, setValue] as const,
      [memoizedValue, setValue],
    );

    return (
      <ParamContext.Provider value={contextValue}>
        {children}
      </ParamContext.Provider>
    );
  };

  const useParams = () => useContext(ParamContext);

  return {
    Provider,
    Consumer: ParamContext.Consumer,
    useParams,
  };
}

export const {
  Provider: ProvideSearchQueryParam,
  Consumer: ConsumeSearchQueryParam,
  useParams: useSearchQueryParam,
} = createParamContext(makeStringParser('query'));
